/**
 * Switch.tsx — 开关原子（受控 / 单实现）
 *
 * 移植自原型 design/Novel-admin/src/primitives.jsx 的 window.UI.Switch，逐属性 1:1：
 *   - checked / on  → 任一作为受控值（checked 优先，回退 on），prop 变化即同步内部态
 *   - onChange(next)→ 切换回调，传下一个布尔值
 *   - disabled / locked → 阻止切换；locked 额外在轨道内渲染锁图标
 *   - size 'md'(32×18 默认) | 'sm'(30×17)：尺寸 / 旋钮 / 位移逐字对齐蓝本
 *
 * 视觉（与蓝本逐字一致）：
 *   - 轨道圆角 999；开 var(--accent) / 关 var(--border-strong)；background 0.15s 过渡
 *   - 旋钮 white，top:2，left 在 2 ↔ d.x 间 0.15s ease 滑动，带 0 1px 2px rgba(0,0,0,0.15) 投影
 *   - blocked(disabled||locked) → opacity 0.55 + cursor not-allowed
 *   - locked 锁图标用 IconLock size=8，开/关时左右定位互换，色 var(--bg-elev)
 */

import { useEffect, useState } from 'react';
import { Icons } from '~/lib/icons';

export interface SwitchProps {
  /** 受控值（优先于 on） */
  checked?: boolean;
  /** 受控值别名（checked 缺省时回退到它） */
  on?: boolean;
  /** 切换回调，传下一个布尔值 */
  onChange?: (next: boolean) => void;
  /** 禁用：阻止切换 + 置灰 */
  disabled?: boolean;
  /** 锁定：阻止切换 + 置灰 + 轨道内渲染锁图标 */
  locked?: boolean;
  /** 尺寸档位，默认 'md' */
  size?: 'sm' | 'md';
  /**
   * 可访问名。本组件渲染的是只带 `aria-pressed` 的裸 `<button>` —— 旁边那行文字**不会**
   * 自动关联上来。同一页出现多个开关时（如按 App 配置 7 个任务的开放态），读屏与语音控制
   * 用户拿到的是 7 个无法区分的按钮。有并列同类开关时必须传。
   */
  ariaLabel?: string;
}

export function Switch({
  checked,
  on,
  onChange,
  disabled,
  locked,
  size = 'md',
  ariaLabel,
}: SwitchProps) {
  const initial = (checked !== undefined ? checked : on) || false;
  const [v, setV] = useState(initial);
  // prop 变化即同步内部态（受控语义）
  useEffect(() => {
    setV(initial);
  }, [initial]);

  // 尺寸 / 旋钮直径 / 旋钮开态位移，逐字对齐蓝本
  const d = size === 'sm' ? { w: 30, h: 17, knob: 13, x: 15 } : { w: 32, h: 18, knob: 14, x: 16 };
  const blocked = disabled || locked;
  const toggle = () => {
    if (blocked) return;
    const nv = !v;
    setV(nv);
    onChange?.(nv);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={blocked}
      aria-pressed={v}
      aria-label={ariaLabel}
      style={{
        width: d.w,
        height: d.h,
        padding: 0,
        borderRadius: 999,
        border: 0,
        background: v ? 'var(--accent)' : 'var(--border-strong)',
        cursor: blocked ? 'not-allowed' : 'pointer',
        position: 'relative',
        transition: 'background 0.15s',
        opacity: blocked ? 0.55 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: v ? d.x : 2,
          width: d.knob,
          height: d.knob,
          borderRadius: 999,
          background: 'white',
          transition: 'left 0.15s ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}
      />
      {locked && (
        <Icons.IconLock
          size={8}
          style={{
            position: 'absolute',
            top: '50%',
            left: v ? 5 : 'auto',
            right: v ? 'auto' : 5,
            transform: 'translateY(-50%)',
            color: 'var(--bg-elev)',
          }}
        />
      )}
    </button>
  );
}
